/**
 * Bundled fixtures for demo mode (`PUBLIC_DEMO=1`).
 *
 * `apiFetch` short-circuits to `resolveDemoFixture(path)` when `DEMO`
 * is on (see `./api.ts`), so the real dashboard components render
 * against this seeded, read-only dataset instead of the live API. No
 * network, no auth, no cost — a bot hammering `/embed` just re-reads
 * the same in-bundle JSON.
 *
 * The data tells a deliberate story for the marketing surface: a
 * plausible store ("Lumina") with good-but-improvable Core Web Vitals
 * and a visible LCP regression ~100 days ago that a recovery deploy
 * fixed — the deploy markers on the trend curve line up with it. The
 * series is anchored to *today* so the demo never reads as stale.
 *
 * Fictional brand, invented metrics. Not a real store, not real data.
 */
import { DEMO_SITE_UUID } from './demo';
import type { DeployMarker } from './queries/deploys';
import type {
  PixelBreakdownRow,
  PixelHealthToday,
  PixelLive,
  PixelPageSessionRow,
  PixelPageTrafficRow,
  PixelPageTrafficTrendRow,
  PixelPerfByCountryRow,
  PixelPerfByPageRow,
  PixelPerfRow,
  PixelPerfTrendRow,
  PixelProfileRow,
  PixelSessionEventRow,
  PixelWorstSessionRow,
} from './queries/pixel';

// ─── The seeded site ──────────────────────────────────────────────────────

const SITE = {
  uuid: DEMO_SITE_UUID,
  slug: 'lumina',
  name: 'Lumina',
  url: 'https://lumina.store',
  team_uuid: null,
  organisation_uuid: 'org-lumina',
  is_public: false,
  monitoring_enabled: true,
  created_at: '2024-10-02T09:00:00Z',
  updated_at: '2024-10-02T09:00:00Z',
  // Audit config (drives /settings/sites). Regions + devices reference
  // AUDIT_REGIONS / DEVICE_PROFILES below by uuid.
  audit: {
    regions: [{ uuid: 'rg-usc1', label: 'US Central (Iowa)' }],
    devices: [
      { uuid: 'dp-mobile', label: 'Moto G Power' },
      { uuid: 'dp-desktop', label: 'Desktop' },
    ],
    schedule: '0 3 * * *',
    paused_at: null,
    next_run_at: dayOffset(-1).toISOString(),
    last_run_at: dayOffset(0).toISOString(),
    grid_size: 2,
    notification_email: ['demo@lumina.store'],
    notification_slack: null,
    webhook_url: null,
  },
};

// ─── Deterministic day series ─────────────────────────────────────────────

type FF = 'phone' | 'desktop';
const FORM_FACTORS: readonly FF[] = ['phone', 'desktop'];

/** Baseline p75s — ms for the time metrics, unitless for CLS. Phone is
 *  deliberately worse than desktop, as real field data almost always is. */
const BASE: Record<FF, { lcp: number; cls: number; inp: number; ttfb: number; fcp: number }> = {
  phone: { lcp: 2350, cls: 0.08, inp: 184, ttfb: 760, fcp: 1680 },
  desktop: { lcp: 1650, cls: 0.04, inp: 116, ttfb: 520, fcp: 1120 },
};

/** The "good" upper bound per metric — used to derive a believable
 *  good-density from the p75 (dips during the regression). */
const GOOD = { lcp: 2500, cls: 0.1, inp: 200, ttfb: 800, fcp: 1800 } as const;

/** Regression window (days-ago), anchored to today so the story stays
 *  visible whenever the demo is viewed. A deploy pushed LCP into "poor"
 *  for ~2 weeks; a recovery deploy fixed it. */
const REGRESSION_START = 104;
const REGRESSION_END = 90;

/** Deterministic 0..1 from a string seed — stable within a day, gives
 *  believable jitter across days without `Math.random`. */
function seeded(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return ((h >>> 0) % 10000) / 10000;
}

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** UTC midnight `daysAgo` before today. */
function dayOffset(daysAgo: number): Date {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d;
}

function density(p75: number, goodThreshold: number): number {
  const ratio = goodThreshold / Math.max(p75, 1);
  return Math.max(0.15, Math.min(0.98, Math.round(ratio * 0.75 * 100) / 100));
}

function metricsForDay(ff: FF, daysAgo: number, dayStr: string) {
  const b = BASE[ff];
  const inRegression = daysAgo <= REGRESSION_START && daysAgo >= REGRESSION_END;
  const jitter = (m: string): number => 1 + (seeded(`${dayStr}:${ff}:${m}`) - 0.5) * 0.08; // ±4%
  const lcp = Math.round(b.lcp * (inRegression ? 1.34 : 1) * jitter('lcp'));
  const fcp = Math.round(b.fcp * (inRegression ? 1.16 : 1) * jitter('fcp'));
  const ttfb = Math.round(b.ttfb * (inRegression ? 1.1 : 1) * jitter('ttfb'));
  const inp = Math.round(b.inp * jitter('inp'));
  const cls = Math.round(b.cls * jitter('cls') * 1000) / 1000;
  return { lcp, cls, inp, ttfb, fcp };
}

function makeRow(
  ff: FF,
  m: { lcp: number; cls: number; inp: number; ttfb: number; fcp: number },
  sampleCount: number,
): PixelPerfRow {
  return {
    form_factor: ff,
    lcp_p75: m.lcp,
    lcp_good_density: density(m.lcp, GOOD.lcp),
    lcp_sample_count: sampleCount,
    cls_p75: m.cls,
    cls_good_density: density(m.cls, GOOD.cls),
    cls_sample_count: sampleCount,
    inp_p75: m.inp,
    inp_good_density: density(m.inp, GOOD.inp),
    inp_sample_count: sampleCount,
    ttfb_p75: m.ttfb,
    ttfb_good_density: density(m.ttfb, GOOD.ttfb),
    ttfb_sample_count: sampleCount,
    fcp_p75: m.fcp,
    fcp_good_density: density(m.fcp, GOOD.fcp),
    fcp_sample_count: sampleCount,
    loaf_p75: ff === 'phone' ? 220 : 140,
  };
}

/** Per-day samples with gentle weekday-ish variation. */
function daySamples(ff: FF, dayStr: string): number {
  const base = ff === 'phone' ? 240 : 165;
  return Math.round(base * (0.8 + seeded(`${dayStr}:${ff}:n`) * 0.5));
}

function buildTrend(days: number): PixelPerfTrendRow[] {
  const rows: PixelPerfTrendRow[] = [];
  for (let daysAgo = days - 1; daysAgo >= 0; daysAgo -= 1) {
    const dayStr = ymd(dayOffset(daysAgo));
    for (const ff of FORM_FACTORS) {
      const m = metricsForDay(ff, daysAgo, dayStr);
      rows.push({ day: dayStr, ...makeRow(ff, m, daySamples(ff, dayStr)) });
    }
  }
  return rows;
}

/** Current-window aggregate `perf` rows — today's (post-recovery)
 *  metrics with a cumulative sample count over the window. */
/**
 * Site perf, honouring the same filters the real endpoint takes.
 *
 * The filters used to be ignored here, which was invisible until traffic
 * profiles started calling this per-profile: every card rendered the same
 * three rows, so a phone-only profile showed a Desktop line. In the real
 * API the filters reach `pixel_site_perf_filtered`, so a fixture that
 * drops them isn't a simplification — it shows something that cannot
 * happen.
 *
 * Country and network don't change which rows come back, only how bad
 * they are: narrowing to slow connections should visibly hurt, or the
 * demo teaches the opposite of the product's point.
 */
function buildPerf(
  days: number,
  filters: {
    formFactors?: string[];
    countryCodes?: string[];
    networkTypes?: string[];
    saveData?: number;
    regionCodes?: string[];
    browserFamilies?: string[];
    osFamilies?: string[];
    channels?: string[];
    referrerHosts?: string[];
    utmSources?: string[];
    utmMediums?: string[];
    utmCampaigns?: string[];
    pagePaths?: string[];
  } = {},
): PixelPerfRow[] {
  const scale = Math.max(1, Math.round(days * 0.9));
  const wanted = filters.formFactors?.length ? new Set(filters.formFactors) : null;

  // Worst connection class named decides the penalty — a profile covering
  // 3g and 2g is dominated by the 2g experience.
  const slowest = (filters.networkTypes ?? []).reduce(
    (worst, n) => Math.max(worst, { '4g': 0, '3g': 1, '2g': 2, 'slow-2g': 3 }[n] ?? 0),
    0,
  );
  const penalty = [1, 1.9, 3.1, 4.4][slowest] ?? 1;
  // Reach of the named connection classes, as a share of all traffic.
  // Additive on purpose: a filter naming 3g + 2g + slow-2g must return
  // the sample count of the three measured separately, because that is
  // what `network_type IN (...)` does against real events. Deriving reach
  // from the *penalty* instead (which is what this did) made a narrow
  // slow-2g slice out-count the wider segment containing it — invisible
  // until the segment detail page put a segment and its parts in one
  // table.
  const NETWORK_SHARE: Record<string, number> = {
    '4g': 0.62,
    '3g': 0.24,
    '2g': 0.1,
    'slow-2g': 0.04,
  };
  const networkReach = filters.networkTypes?.length
    ? filters.networkTypes.reduce((sum, n) => sum + (NETWORK_SHARE[n] ?? 0), 0)
    : 1;
  // A country filter narrows reach without a systematic vitals effect;
  // so does every other non-device dimension. Applied generically so a
  // narrow profile never renders the whole site's sample count under its
  // name — the fixture's job is to be shaped like the real answer.
  const narrowing =
    [
      filters.countryCodes,
      filters.regionCodes,
      filters.browserFamilies,
      filters.osFamilies,
      filters.channels,
      filters.referrerHosts,
      filters.utmSources,
      filters.utmMediums,
      filters.utmCampaigns,
      filters.pagePaths,
    ].filter((l) => l?.length).length + (filters.saveData !== undefined ? 1 : 0);
  const reach = 0.28 ** narrowing;

  return FORM_FACTORS.filter((ff) => !wanted || wanted.has(ff)).map((ff) => {
    const m = metricsForDay(ff, 0, ymd(dayOffset(0)));
    const row = makeRow(ff, m, Math.round(daySamples(ff, 'agg') * scale * reach * networkReach));
    if (penalty === 1) return row;
    return {
      ...row,
      lcp_p75: row.lcp_p75 === null ? null : round2(row.lcp_p75 * penalty),
      inp_p75: row.inp_p75 === null ? null : round2(row.inp_p75 * penalty),
      cls_p75: row.cls_p75 === null ? null : round2(row.cls_p75 * Math.min(penalty, 2.2)),
    };
  });
}

// ─── Rankings + dropdown options ──────────────────────────────────────────

const COUNTRIES: ReadonlyArray<{ code: string; lcp: number; share: number }> = [
  { code: 'US', lcp: 2210, share: 0.34 },
  { code: 'GB', lcp: 2180, share: 0.16 },
  { code: 'DE', lcp: 2090, share: 0.12 },
  { code: 'CA', lcp: 2320, share: 0.09 },
  { code: 'AU', lcp: 2540, share: 0.08 },
  { code: 'FR', lcp: 2270, share: 0.07 },
  { code: 'NL', lcp: 2040, share: 0.06 },
  { code: 'IN', lcp: 3120, share: 0.05 },
  { code: 'BR', lcp: 3260, share: 0.03 },
];

const TOTAL_PV = 486_000;

function buildPerfByCountry(): PixelPerfByCountryRow[] {
  return COUNTRIES.map((c) => {
    const samples = Math.round(TOTAL_PV * c.share);
    return {
      country_code: c.code,
      lcp_p75: c.lcp,
      lcp_sample_count: samples,
      cls_p75: 0.05 + seeded(`${c.code}:cls`) * 0.06,
      cls_sample_count: samples,
      inp_p75: Math.round(140 + seeded(`${c.code}:inp`) * 90),
      inp_sample_count: samples,
      ttfb_p75: Math.round(560 + seeded(`${c.code}:ttfb`) * 380),
      ttfb_sample_count: samples,
      fcp_p75: Math.round(1200 + seeded(`${c.code}:fcp`) * 700),
      fcp_sample_count: samples,
    };
  });
}

// ─── Traffic profiles ─────────────────────────────────────────────────────

/** The demo's audience split. Tells the same story as the rest of the
 *  fixture — Lumina is fine on desktop and struggling on phones, worst
 *  of all for the small slice on a constrained connection, which is
 *  exactly the cohort the Profiles surface exists to make visible. */
const PROFILES: ReadonlyArray<{
  form_factor: string;
  network_type: string;
  save_data: number;
  share: number;
  lcp: number;
  inp: number;
  cls: number;
  poor: number;
}> = [
  {
    form_factor: 'phone',
    network_type: '4g',
    save_data: 0,
    share: 0.41,
    lcp: 2890,
    inp: 214,
    cls: 0.09,
    poor: 0.31,
  },
  {
    form_factor: 'desktop',
    network_type: '4g',
    save_data: 0,
    share: 0.34,
    lcp: 1840,
    inp: 132,
    cls: 0.04,
    poor: 0.08,
  },
  {
    form_factor: 'phone',
    network_type: '3g',
    save_data: 0,
    share: 0.11,
    lcp: 5120,
    inp: 386,
    cls: 0.14,
    poor: 0.72,
  },
  {
    form_factor: 'tablet',
    network_type: '4g',
    save_data: 0,
    share: 0.07,
    lcp: 2410,
    inp: 178,
    cls: 0.06,
    poor: 0.19,
  },
  {
    form_factor: 'phone',
    network_type: '3g',
    save_data: 1,
    share: 0.04,
    lcp: 6340,
    inp: 442,
    cls: 0.18,
    poor: 0.81,
  },
  {
    form_factor: 'phone',
    network_type: '2g',
    save_data: 1,
    share: 0.02,
    lcp: 8760,
    inp: 521,
    cls: 0.21,
    poor: 0.89,
  },
  {
    form_factor: 'desktop',
    network_type: 'unknown',
    save_data: 0,
    share: 0.01,
    lcp: 2020,
    inp: 148,
    cls: 0.05,
    poor: 0.12,
  },
];

const TOTAL_SESSIONS = 128_400;

function buildProfiles(): PixelProfileRow[] {
  return PROFILES.map((p) => {
    const sessions = Math.round(TOTAL_SESSIONS * p.share);
    const samples = Math.round(sessions * 1.8);
    return {
      form_factor: p.form_factor,
      network_type: p.network_type,
      network_save_data: p.save_data,
      sessions,
      samples,
      poor_share: p.poor,
      affected_sessions: sessions * p.poor,
      lcp_p75: p.lcp,
      lcp_good_density: density(p.lcp, 2500),
      lcp_sample_count: samples,
      cls_p75: p.cls,
      cls_good_density: density(p.cls, 0.1),
      cls_sample_count: samples,
      inp_p75: p.inp,
      inp_good_density: density(p.inp, 200),
      inp_sample_count: samples,
      ttfb_p75: Math.round(p.lcp * 0.28),
      ttfb_good_density: density(p.lcp * 0.28, 800),
      ttfb_sample_count: samples,
      fcp_p75: Math.round(p.lcp * 0.52),
      fcp_good_density: density(p.lcp * 0.52, 1800),
      fcp_sample_count: samples,
      loaf_p75: Math.round(120 + seeded(`${p.form_factor}:${p.network_type}:loaf`) * 180),
    };
  }).sort((a, b) => b.affected_sessions - a.affected_sessions);
}

/** Three worst sessions per day, so the trend chart's day picker has
 *  something real to open. Values track the day's p75 but sit above it
 *  — these are the tail, not the typical visit. */
function buildWorstSessions(days: number, metric: string): PixelWorstSessionRow[] {
  const rows: PixelWorstSessionRow[] = [];
  for (let daysAgo = days - 1; daysAgo >= 0; daysAgo -= 1) {
    const dayStr = ymd(dayOffset(daysAgo));
    for (let n = 0; n < 3; n += 1) {
      const key = `${dayStr}:${metric}:${n}`;
      const base = metricsForDay('phone', daysAgo, dayStr);
      const p75 = metric === 'cls' ? base.cls : metric === 'inp' ? base.inp : base.lcp;
      const hour = 8 + Math.floor(seeded(`${key}:h`) * 12);
      const minute = Math.floor(seeded(`${key}:m`) * 60);
      const page = PAGES[Math.floor(seeded(`${key}:p`) * PAGES.length)] ?? PAGES[0]!;
      const country = COUNTRIES[Math.floor(seeded(`${key}:c`) * COUNTRIES.length)] ?? COUNTRIES[0]!;
      rows.push({
        day: dayStr,
        session_uuid: `ses_demo${dayStr.replace(/-/g, '')}${n}`,
        started_at: `${dayStr} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`,
        page_path: page.path,
        form_factor: n === 2 ? 'desktop' : 'phone',
        country_code: country.code,
        network_type: n === 0 ? '3g' : '4g',
        // Tail multiplier: the worst session of a day runs well past
        // the p75 the chart plots, which is the point of opening it.
        value: Number((p75 * (1.6 + seeded(`${key}:v`) * 1.4)).toFixed(metric === 'cls' ? 3 : 0)),
      });
    }
  }
  return rows;
}

/**
 * One session's event stream, seeded from the id so any session the
 * picker or sessions list offers resolves to something coherent.
 *
 * Shaped the way the real pipe returns it: web-vitals arrive as
 * `visibility` beacons that the session view folds onto the pageview
 * they measured (matched on page_path + pageview_count_in_session),
 * and Navigation Timing only rides the hard pageview that opened the
 * session — the soft ones are SPA route changes with nothing to time.
 */
/**
 * Sessions that viewed one page. Shaped like real traffic rather than
 * like a demo: most visits are a single view of the page you're already
 * looking at, on the same browser, arriving the same way. That skew is
 * the point — it's what the sessions list elides so the handful of rows
 * that differ (a second browser, a referral, a visitor who carried on
 * to another page) are the ones that catch the eye.
 */
/**
 * Page-scoped traffic, trend and breakdowns.
 *
 * These three had no fixture at all, so every surface above the sessions
 * table on /traffic/page rendered its empty shell — on the public demo
 * as well as locally. Scaled off the page's share of site traffic so the
 * numbers agree with the site-level KPIs rather than contradicting them
 * a scroll apart.
 */
function pageShareOf(path: string): number {
  const known = PAGES.find((p) => p.path === path || path.endsWith(p.path));
  return known?.share ?? 0.04;
}

function buildPageTraffic(path: string, days: number): PixelPageTrafficRow {
  const share = pageShareOf(path);
  const pageviews = Math.round(TOTAL_PV * share * (days / 180));
  const sessions = Math.round(pageviews * 0.72);
  return {
    pageviews,
    visitors: Math.round(sessions * 0.83),
    sessions,
    entries: Math.round(sessions * 0.46),
    exits: Math.round(sessions * 0.38),
    avg_engagement_ms: Math.round(26_000 + seeded(`${path}:eng`) * 48_000),
  };
}

function buildPageTrafficTrend(path: string, days: number): PixelPageTrafficTrendRow[] {
  const share = pageShareOf(path);
  const rows: PixelPageTrafficTrendRow[] = [];
  for (let daysAgo = days - 1; daysAgo >= 0; daysAgo -= 1) {
    const dayStr = ymd(dayOffset(daysAgo));
    const base = (TOTAL_PV * share) / 180;
    const pageviews = Math.round(base * (0.7 + seeded(`${path}:${dayStr}:pv`) * 0.6));
    const sessions = Math.round(pageviews * 0.72);
    rows.push({
      day: dayStr,
      pageviews,
      entries: Math.round(sessions * 0.46),
      exits: Math.round(sessions * 0.38),
      visitors: Math.round(sessions * 0.83),
      sessions,
    });
  }
  return rows;
}

function buildPageBreakdown(path: string, dimension: string, days: number): PixelBreakdownRow[] {
  const total = buildPageTraffic(path, days).pageviews;
  const buckets: string[] =
    dimension === 'country_code'
      ? COUNTRIES.map((c) => c.code)
      : dimension === 'form_factor'
        ? ['phone', 'desktop', 'tablet']
        : dimension === 'browser_family'
          ? ['chrome', 'safari', 'edge', 'firefox']
          : dimension === 'os_family'
            ? ['iOS', 'Android', 'macOS', 'Windows']
            : ['direct', 'organic', 'social', 'referral', 'email'];
  const weights = buckets.map((b) => 0.1 + seeded(`${path}:${dimension}:${b}`));
  const sum = weights.reduce((a, b) => a + b, 0);
  return buckets
    .map((bucket, i) => {
      const pageviews = Math.round((total * (weights[i] ?? 0)) / sum);
      const sessions = Math.round(pageviews * 0.72);
      return {
        bucket,
        pageviews,
        visitors: Math.round(sessions * 0.83),
        sessions,
        // Raw-backed page breakdowns don't carry vitals — same as prod.
        lcp_p75: null,
        lcp_good_density: null,
      };
    })
    .sort((a, b) => b.pageviews - a.pageviews);
}

function buildPageSessions(path: string, limit: number): PixelPageSessionRow[] {
  const rows: PixelPageSessionRow[] = [];
  for (let i = 0; i < Math.min(limit, 14); i += 1) {
    const key = `${path}:${i}`;
    const r = seeded(key);
    // ~1 in 4 visitors carries on to a second page.
    const wandered = seeded(`${key}:w`) > 0.74;
    const onward = PAGES[Math.floor(seeded(`${key}:o`) * PAGES.length)] ?? PAGES[0]!;
    const views = wandered ? 2 + Math.floor(seeded(`${key}:v`) * 8) : 1;
    const startedAt = dayOffset(Math.floor(r * 7));
    startedAt.setUTCHours(8 + Math.floor(seeded(`${key}:h`) * 12), Math.floor(r * 60), 0, 0);
    const durationMs = wandered ? Math.round(20_000 + seeded(`${key}:d`) * 180_000) : 0;
    const endedAt = new Date(startedAt.getTime() + durationMs);
    const phone = seeded(`${key}:ff`) > 0.55;
    const reported = seeded(`${key}:rep`) > 0.25; // Chromium-only vitals
    const lcp = Math.round((phone ? 2600 : 1800) * (0.6 + seeded(`${key}:lcp`) * 1.4));
    const chStamp = (d: Date): string => d.toISOString().slice(0, 19).replace('T', ' ');
    rows.push({
      session_uuid: `ses_demo${path.replace(/\W+/g, '') || 'root'}${i}`,
      // A minority of visitors are signed in, so the list shows both
      // states: a durable identified user and a daily device hash.
      // Device ids come from a small pool so the same visitor shows up
      // on more than one row — the property that makes the column
      // worth looking at.
      profile_uuid:
        seeded(`${key}:pid`) > 0.7 ? `pf${seeded(`${key}:pf`).toString(36).slice(2, 14)}` : '',
      device_uuid: `dv${seeded(`${path}:${i % 5}:dev`)
        .toString(36)
        .slice(2, 14)}`,
      started_at: chStamp(startedAt),
      ended_at: chStamp(endedAt),
      entry_path: path,
      exit_path: wandered ? onward.path : path,
      pageviews: views,
      engagement_ms: durationMs,
      country_code: (COUNTRIES[Math.floor(seeded(`${key}:c`) * COUNTRIES.length)] ?? COUNTRIES[0]!)
        .code,
      form_factor: phone ? 'phone' : 'desktop',
      // Deliberately lopsided: Chrome unless the seed says otherwise.
      browser_family: seeded(`${key}:b`) > 0.88 ? 'edge' : 'chrome',
      channel: seeded(`${key}:ch`) > 0.82 ? 'social' : 'direct',
      referrer_host: seeded(`${key}:ch`) > 0.82 ? 'instagram.com' : '',
      lcp: reported ? lcp : null,
      cls: reported ? Math.round(seeded(`${key}:cls`) * 90) / 1000 : null,
      inp: reported ? Math.round(60 + seeded(`${key}:inp`) * 180) : null,
    });
  }
  return rows.sort((a, b) => b.started_at.localeCompare(a.started_at));
}

function buildSessionEvents(sessionId: string, around: string): PixelSessionEventRow[] {
  const day = (around || `${ymd(dayOffset(1))} 12:00:00`).slice(0, 10);
  const startHour = 8 + Math.floor(seeded(`${sessionId}:h`) * 12);
  const pageCount = 2 + Math.floor(seeded(`${sessionId}:n`) * 2);
  const country =
    COUNTRIES[Math.floor(seeded(`${sessionId}:c`) * COUNTRIES.length)] ?? COUNTRIES[0]!;
  const phone = seeded(`${sessionId}:ff`) > 0.4;
  const slow = seeded(`${sessionId}:net`) > 0.6;

  const context = {
    country_code: country.code,
    region_code: '',
    form_factor: phone ? 'phone' : 'desktop',
    browser_family: phone ? 'Chrome Mobile' : 'Chrome',
    os_family: phone ? 'Android' : 'macOS',
    network_type: slow ? '3g' : '4g',
    network_save_data: slow && seeded(`${sessionId}:sd`) > 0.6 ? 1 : 0,
    channel: 'organic',
    referrer_host: 'google.com',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
  };

  const stamp = (minutes: number): string =>
    `${day} ${String(startHour + Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:00`;

  const empty = {
    event_name: '',
    event_props: '',
    outgoing_host: '',
    lcp: null,
    cls: null,
    inp: null,
    ttfb: null,
    fcp: null,
    lcp_rating: '',
    cls_rating: '',
    inp_rating: '',
    ttfb_rating: '',
    fcp_rating: '',
    longest_loaf_ms: null,
    loaf_script_host: '',
    nav_dns_ms: null,
    nav_tcp_ms: null,
    nav_request_ms: null,
    nav_response_ms: null,
    nav_dom_interactive_ms: null,
    nav_dom_complete_ms: null,
    nav_redirect_count: 0,
    nav_type: '',
    bfcache_hit: 0,
  };

  const rows: PixelSessionEventRow[] = [];
  for (let i = 1; i <= pageCount; i += 1) {
    const page = PAGES[Math.floor(seeded(`${sessionId}:p${i}`) * PAGES.length)] ?? PAGES[0]!;
    const hard = i === 1;
    // The slow-connection sessions are the ones worth opening: their
    // response phase dominates and LCP lands well past the threshold.
    const lcp = Math.round(page.lcp * (slow ? 2.4 : 1.05) * (phone ? 1.2 : 1));
    rows.push({
      ...empty,
      ...context,
      timestamp: stamp((i - 1) * 3),
      event_kind: hard ? 'pageview' : 'pageview_soft',
      page_path: page.path,
      engagement_ms: Math.round(18_000 + seeded(`${sessionId}:e${i}`) * 90_000),
      scroll_depth_pct: [25, 50, 75, 100][Math.floor(seeded(`${sessionId}:s${i}`) * 4)] ?? 50,
      active_visible_ms: Math.round(15_000 + seeded(`${sessionId}:a${i}`) * 70_000),
      is_session_start: hard ? 1 : 0,
      is_session_end: 0,
      pageview_count_in_session: i,
      ...(hard
        ? {
            nav_dns_ms: Math.round(8 + seeded(`${sessionId}:dns`) * 40),
            nav_tcp_ms: Math.round(20 + seeded(`${sessionId}:tcp`) * (slow ? 180 : 60)),
            nav_request_ms: Math.round(40 + seeded(`${sessionId}:req`) * (slow ? 320 : 90)),
            nav_response_ms: Math.round(90 + seeded(`${sessionId}:res`) * (slow ? 900 : 200)),
            nav_dom_interactive_ms: Math.round(lcp * 0.62),
            nav_dom_complete_ms: Math.round(lcp * 1.14),
            nav_type: 'navigate',
          }
        : {}),
    });
    // The vitals beacon for that page lifecycle.
    rows.push({
      ...empty,
      ...context,
      timestamp: stamp((i - 1) * 3 + 2),
      event_kind: 'visibility',
      page_path: page.path,
      engagement_ms: 0,
      scroll_depth_pct: 0,
      active_visible_ms: 0,
      is_session_start: 0,
      is_session_end: i === pageCount ? 1 : 0,
      pageview_count_in_session: i,
      lcp,
      lcp_rating: lcp <= 2500 ? 'good' : lcp <= 4000 ? 'needs-improvement' : 'poor',
      cls: Math.round(seeded(`${sessionId}:cls${i}`) * (slow ? 220 : 90)) / 1000,
      cls_rating: slow ? 'needs-improvement' : 'good',
      inp: Math.round(90 + seeded(`${sessionId}:inp${i}`) * (slow ? 420 : 130)),
      inp_rating: slow ? 'poor' : 'good',
      ttfb: Math.round(lcp * 0.3),
      ttfb_rating: lcp * 0.3 <= 800 ? 'good' : 'needs-improvement',
      fcp: Math.round(lcp * 0.55),
      fcp_rating: lcp * 0.55 <= 1800 ? 'good' : 'needs-improvement',
      longest_loaf_ms: Math.round(90 + seeded(`${sessionId}:loaf${i}`) * (slow ? 380 : 140)),
      loaf_script_host:
        seeded(`${sessionId}:lh${i}`) > 0.5 ? 'cdn.lumina.store' : 'connect.facebook.net',
    });
    // The pagehide flush that closes the lifecycle. Carries no metric of
    // its own — the session view hides it unless ?raw=1.
    rows.push({
      ...empty,
      ...context,
      timestamp: stamp((i - 1) * 3 + 2),
      event_kind: 'pageend',
      event_name: 'pageend',
      page_path: page.path,
      engagement_ms: 0,
      scroll_depth_pct: 0,
      active_visible_ms: 0,
      is_session_start: 0,
      is_session_end: i === pageCount ? 1 : 0,
      pageview_count_in_session: i,
    });
  }
  // A parting outbound click, so the timeline shows more than pageviews.
  rows.push({
    ...empty,
    ...context,
    timestamp: stamp(pageCount * 3 + 1),
    event_kind: 'outbound_click',
    page_path: rows[rows.length - 1]?.page_path ?? '/',
    engagement_ms: 0,
    scroll_depth_pct: 0,
    active_visible_ms: 0,
    outgoing_host: 'instagram.com',
    is_session_start: 0,
    is_session_end: 0,
    pageview_count_in_session: pageCount,
  });
  return rows;
}

const PAGES: ReadonlyArray<{ path: string; lcp: number; share: number }> = [
  { path: '/', lcp: 2080, share: 0.28 },
  { path: '/collections/lighting', lcp: 2460, share: 0.19 },
  { path: '/products/aurora-floor-lamp', lcp: 2920, share: 0.14 },
  { path: '/products/halo-pendant', lcp: 2610, share: 0.1 },
  { path: '/cart', lcp: 1980, share: 0.08 },
  { path: '/checkout', lcp: 1740, share: 0.07 },
  { path: '/collections/new-in', lcp: 2530, share: 0.06 },
  { path: '/pages/our-story', lcp: 2210, share: 0.05 },
  { path: '/blog/how-to-layer-light', lcp: 2350, share: 0.03 },
];

function buildPerfByPage(): PixelPerfByPageRow[] {
  return PAGES.map((p) => {
    const pageviews = Math.round(TOTAL_PV * p.share);
    const samples = Math.round(pageviews * 0.62);
    return {
      page_path: p.path,
      pageviews,
      lcp_p75: p.lcp,
      lcp_sample_count: samples,
      cls_p75: 0.04 + seeded(`${p.path}:cls`) * 0.07,
      cls_sample_count: samples,
      inp_p75: Math.round(130 + seeded(`${p.path}:inp`) * 110),
      inp_sample_count: samples,
      ttfb_p75: Math.round(520 + seeded(`${p.path}:ttfb`) * 340),
      ttfb_sample_count: samples,
      fcp_p75: Math.round(1150 + seeded(`${p.path}:fcp`) * 720),
      fcp_sample_count: samples,
    };
  });
}

function buildBreakdown(dimension: string): PixelBreakdownRow[] {
  if (dimension === 'country_code') {
    return COUNTRIES.map((c) => {
      const pageviews = Math.round(TOTAL_PV * c.share);
      return {
        bucket: c.code,
        pageviews,
        visitors: Math.round(pageviews * 0.72),
        sessions: Math.round(pageviews * 0.81),
        lcp_p75: c.lcp,
        lcp_good_density: density(c.lcp, GOOD.lcp),
      };
    });
  }
  return [];
}

// ─── Deploy markers (aligned to the regression story) ─────────────────────

function iso(daysAgo: number): string {
  const d = dayOffset(daysAgo);
  d.setUTCHours(14, 12, 0, 0);
  return d.toISOString();
}

function buildDeploys(): DeployMarker[] {
  const mk = (
    n: string,
    daysAgo: number,
    branch: string,
    sha: string,
    note: string,
  ): DeployMarker => ({
    uuid: `dpm-demo${n}`,
    site_uuid: DEMO_SITE_UUID,
    deploy_id: `dep_${sha}`,
    commit_sha: sha,
    branch,
    deployer: 'ci',
    provider: 'vercel',
    deploy_url: null,
    metadata: { note },
    created_at: iso(daysAgo),
  });
  return [
    mk('4', 6, 'main', 'a1f9c02', 'Homepage hero refresh'),
    mk('3', 48, 'main', 'e77b310', 'Checkout copy + analytics'),
    mk('2', REGRESSION_END, 'main', 'b3d0a91', 'Revert hero video, lazy-load carousel'),
    mk('1', REGRESSION_START, 'main', '9c4e77a', 'New homepage hero video'),
  ];
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ─── Workspace / entitlement (unlocks the paid CrUX geo + history) ─────────

function buildUsage(): unknown {
  return {
    plan: 'pro',
    plan_limits: { included_credits: 100_000, included_pixel_events: 6_000_000 },
    credits_used: 18_420,
    credits_remaining: 81_580,
    pixel_events_mtd: 486_000,
  };
}

// Audit-region catalogue (embedded in /v1/workspace → `useRegions`) +
// device-profile pool (/v1/management/device-profiles). Both drive the
// audit-config form at /settings/sites.
// Mirrors the production catalogue (fixture uuids, real names) so the
// demo's region picker matches what customers actually see.
const AUDIT_REGIONS = [
  {
    uuid: 'rg-apne1',
    name: 'Asia Northeast 1 (Tokyo)',
    provider: 'gcp',
    location: 'Tokyo, Japan',
    gcp_region: 'asia-northeast1',
    enabled: true,
  },
  {
    uuid: 'rg-euw2',
    name: 'Europe West 2 (London)',
    provider: 'gcp',
    location: 'London, United Kingdom',
    gcp_region: 'europe-west2',
    enabled: true,
  },
  {
    uuid: 'rg-usc1',
    name: 'US Central (Iowa)',
    provider: 'gcp',
    location: 'Iowa, United States',
    gcp_region: 'us-central1',
    enabled: true,
  },
];

const DEVICE_PROFILES = [
  {
    uuid: 'dp-mobile',
    label: 'Moto G Power',
    description: 'Moto G Power · Slow 4G · 4× CPU',
    preset_key: 'moto-g-power',
    source: 'stock',
    device_type: 'mobile',
    network_preset: 'slow-4g',
    network_label: 'Slow 4G',
    download_kbps: 1638,
    upload_kbps: 768,
    rtt_ms: 150,
    cpu_slowdown: 4,
    viewport_width: 412,
    viewport_height: 823,
  },
  {
    uuid: 'dp-desktop',
    label: 'Desktop',
    description: 'Desktop · Cable · No throttle',
    preset_key: 'desktop',
    source: 'stock',
    device_type: 'desktop',
    network_preset: 'cable',
    network_label: 'Cable',
    download_kbps: 5120,
    upload_kbps: 1024,
    rtt_ms: 28,
    cpu_slowdown: 1,
    viewport_width: 1350,
    viewport_height: 940,
  },
];

function buildWorkspace(): unknown {
  return {
    user: { uuid: 'user_demo00000000', email: 'demo@lumina.store', name: 'Demo Viewer' },
    organisations: [
      {
        uuid: 'org-lumina',
        auth_provider_id: 'org_demo00000000',
        name: 'Lumina',
        slug: 'lumina',
        plan: 'pro',
        plan_limits: { included_credits: 100_000, included_pixel_events: 6_000_000 },
        teams: [],
      },
    ],
    active_usage: buildUsage(),
    regions: AUDIT_REGIONS,
  };
}

// ─── CrUX (Field data tab) ─────────────────────────────────────────────────

function cruxDensities(good: number, ni: number) {
  const g = Math.max(0.05, Math.min(0.97, good));
  const n = Math.max(0.02, Math.min(1 - g - 0.02, ni));
  return { good: round2(g), ni: round2(n), poor: round2(1 - g - n) };
}

function cruxPeriodRow(weeksAgo: number, ff: FF): Record<string, number | string> {
  const end = ymd(dayOffset(weeksAgo * 7));
  const start = ymd(dayOffset(weeksAgo * 7 + 27));
  const m = metricsForDay(ff, weeksAgo * 7, end); // reuse perf generator (incl. regression)
  const lcp = cruxDensities(m.lcp < 2500 ? 0.79 : 0.55, 0.16);
  const inp = cruxDensities(0.87, 0.1);
  const cls = cruxDensities(0.91, 0.06);
  const fcp = cruxDensities(m.fcp < 1800 ? 0.82 : 0.62, 0.15);
  const ttfb = cruxDensities(0.71, 0.22);
  return {
    period_start: start,
    period_end: end,
    lcp_p75: m.lcp,
    lcp_good: lcp.good,
    lcp_ni: lcp.ni,
    lcp_poor: lcp.poor,
    inp_p75: m.inp,
    inp_good: inp.good,
    inp_ni: inp.ni,
    inp_poor: inp.poor,
    cls_p75: m.cls,
    cls_good: cls.good,
    cls_ni: cls.ni,
    cls_poor: cls.poor,
    fcp_p75: m.fcp,
    fcp_good: fcp.good,
    fcp_ni: fcp.ni,
    fcp_poor: fcp.poor,
    ttfb_p75: m.ttfb,
    ttfb_good: ttfb.good,
    ttfb_ni: ttfb.ni,
    ttfb_poor: ttfb.poor,
    ff_phone: ff === 'phone' ? 0.62 : 0.6,
    ff_desktop: 0.33,
    ff_tablet: 0.05,
  };
}

function buildCrux(formFactor: string): unknown {
  const ff: FF = formFactor === 'DESKTOP' ? 'desktop' : 'phone';
  const series: Array<Record<string, number | string>> = [];
  for (let w = 25; w >= 0; w -= 1) series.push(cruxPeriodRow(w, ff));
  const score = formFactor === 'DESKTOP' ? 93 : formFactor === 'PHONE' ? 82 : 86;
  return {
    coverage: 'ok',
    last_fetched_at: new Date().toISOString(),
    form_factor: formFactor === 'PHONE' || formFactor === 'DESKTOP' ? formFactor : 'ALL',
    field_score: score,
    cwv: score >= 75 ? 'pass' : 'fail',
    latest: series[series.length - 1],
    series,
  };
}

function buildCruxPages(): unknown {
  const pages = PAGES.slice(0, 6).map((p, i) => ({
    page_uuid: `pg-demo${i}`,
    name: p.path === '/' ? 'Home' : (p.path.split('/').pop() ?? p.path),
    url: `https://lumina.store${p.path}`,
    period_end: ymd(dayOffset(0)),
    field_score: Math.round(70 + seeded(p.path) * 26),
    cwv: p.lcp < 2600 ? 'pass' : 'fail',
    lcp_p75: p.lcp,
    inp_p75: 150 + Math.round(seeded(`${p.path}i`) * 80),
    cls_p75: round2(0.04 + seeded(`${p.path}c`) * 0.08),
    ttfb_p75: 560 + Math.round(seeded(`${p.path}t`) * 300),
    fcp_p75: 1200 + Math.round(seeded(`${p.path}f`) * 600),
  }));
  return { pages, monitored_count: pages.length };
}

function buildCruxGeo(): unknown {
  const countries = COUNTRIES.map((c) => ({
    country_code: c.code.toLowerCase(),
    field_score: Math.round(66 + seeded(`${c.code}g`) * 28),
    cwv: c.lcp < 2600 ? 'pass' : 'fail',
    lcp_p75: c.lcp,
    inp_p75: 150 + Math.round(seeded(c.code) * 80),
    cls_p75: round2(0.04 + seeded(`${c.code}c`) * 0.07),
    ttfb_p75: 560 + Math.round(seeded(`${c.code}t`) * 300),
    fcp_p75: 1200 + Math.round(seeded(`${c.code}f`) * 600),
  }));
  return { coverage: 'ok', last_fetched_at: new Date().toISOString(), countries };
}

function buildCruxHistory(): unknown {
  const series: Array<Record<string, number | string>> = [];
  for (let mo = 23; mo >= 0; mo -= 1) {
    const d = dayOffset(mo * 30);
    const yyyymm = ymd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
    const m = metricsForDay('phone', mo * 30, ymd(d));
    series.push({
      yyyymm,
      rank: 500_000 - (23 - mo) * 2600 + Math.round(seeded(`r${mo}`) * 6000),
      lcp_p75: m.lcp,
      lcp_good: 0.79,
      lcp_ni: 0.15,
      lcp_poor: 0.06,
      inp_p75: m.inp,
      inp_good: 0.87,
      inp_ni: 0.1,
      inp_poor: 0.03,
      cls_p75: m.cls,
      cls_good: 0.91,
      cls_ni: 0.06,
      cls_poor: 0.03,
      fcp_p75: m.fcp,
      fcp_good: 0.82,
      fcp_ni: 0.14,
      fcp_poor: 0.04,
      ttfb_p75: m.ttfb,
      ttfb_good: 0.71,
      ttfb_ni: 0.21,
      ttfb_poor: 0.08,
      ff_phone: 0.62,
      ff_desktop: 0.33,
      ff_tablet: 0.05,
    });
  }
  const latest = series[series.length - 1];
  return {
    coverage: 'ok',
    last_fetched_at: new Date().toISOString(),
    rank: latest?.rank ?? null,
    field_score: 86,
    cwv: 'pass',
    latest,
    series,
  };
}

// ─── Synthetic audits (Audits tab) ─────────────────────────────────────────

function buildMetricsTrend(days: number): unknown {
  const rows: Array<Record<string, number | string>> = [];
  for (let d = Math.min(days, 90) - 1; d >= 0; d -= 1) {
    const dayStr = ymd(dayOffset(d));
    const m = metricsForDay('phone', d, dayStr);
    rows.push({
      bucket: dayStr,
      device_profile_label: 'Moto G Power',
      region: 'us-central1',
      avg_lcp: m.lcp,
      p75_lcp: Math.round(m.lcp * 1.08),
      avg_cls: m.cls,
      p75_cls: m.cls,
      avg_inp: m.inp,
      p75_inp: m.inp,
      avg_ttfb: m.ttfb,
      p75_ttfb: m.ttfb,
      avg_fcp: m.fcp,
      p75_fcp: m.fcp,
      avg_performance_score: m.lcp < 2500 ? 0.91 : 0.77,
      avg_accessibility_score: 0.96,
      avg_best_practices_score: 0.92,
      // A story, not noise: steady ~0.93, a two-week dip to ~0.79
      // (the "bad deploy"), recovery — so the /seo trend demos the
      // deploy-attribution pitch at a glance.
      avg_seo_score: (d > 12 && d < 26 ? 0.79 : 0.93) + ((d * 3) % 5) / 100 - 0.02,
      sample_count: 3,
    });
  }
  return rows;
}

/** One synthetic audit — shared by the list AND the detail endpoint so
 *  /performance/audits/aud_demoN always agrees with the table row. */
function buildAuditEntry(n: number): Record<string, unknown> | null {
  if (n < 1 || n > 40) return null;
  const d = Math.floor((n - 1) / 2);
  const device = (n - 1) % 2 === 0 ? 'mobile' : 'desktop';
  const p = PAGES[d % PAGES.length]!;
  const gen: FF = device === 'mobile' ? 'phone' : 'desktop';
  const m = metricsForDay(gen, d, ymd(dayOffset(d)));
  return {
    id: `aud_demo${n}`,
    url: `https://lumina.store${p.path}`,
    status: 'success',
    device,
    region: 'us-central1',
    device_profile_label: device === 'mobile' ? 'Moto G Power' : 'Desktop',
    created_at: iso(d),
    trigger: 'scheduled',
    ids: {
      snapshot_uuid: `snp-${n}`,
      site_uuid: DEMO_SITE_UUID,
      page_uuid: `pg-demo${d % 6}`,
      device_profile_uuid: device === 'mobile' ? 'dp-mobile' : 'dp-desktop',
      region_uuid: 'rg-usc1',
    },
    // API shape: 0–100 integers on the row (the raw LHR keeps 0–1).
    scores: {
      performance: m.lcp < 2500 ? 92 : 78,
      accessibility: 96,
      best_practices: 92,
      seo: 100,
    },
    metrics: {
      lcp_ms: m.lcp,
      cls: m.cls,
      inp_ms: m.inp,
      tbt_ms: Math.round(m.inp * 1.4),
      fcp_ms: m.fcp,
      si_ms: Math.round(m.fcp * 1.6),
      ttfb_ms: m.ttfb,
    },
  };
}

function buildAudits(): unknown {
  const audits: Array<Record<string, unknown>> = [];
  for (let n = 1; n <= 40; n += 1) {
    const a = buildAuditEntry(n);
    if (a) audits.push(a);
  }
  return { audits, limit: 500, offset: 0, has_more: false };
}

// ─── Lighthouse report (audit drill-in page) ────────────────────────────────
//
// A miniature LH 13-shaped report covering exactly the audits the detail
// page consumes: lab metrics (simulated + observed), the network
// waterfall, LCP element + phase breakdown, main-thread work, the
// critical request chain, render-blocking set, third-party summary,
// filmstrip thumbnails, and category auditRefs for the score breakdown +
// opportunities/diagnostics lists. Values derive from the same
// metricsForDay() stream as the audit row, so tiles and report agree.

/** 1×1 warm-grey PNG — filmstrip frames (kept tiny; the strip scales). */
const FRAME_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4nGP4d+3GfwAJmAPCP047WwAAAABJRU5ErkJggg==';

function buildLighthouseReport(n: number): unknown {
  const entry = buildAuditEntry(n);
  if (!entry) return null;
  const metrics = entry.metrics as Record<string, number>;
  const url = entry.url as string;
  const origin = 'https://lumina.store';
  const lcp = metrics.lcp_ms ?? 2100;
  const fcp = metrics.fcp_ms ?? 1400;
  const ttfb = metrics.ttfb_ms ?? 600;
  const tbt = metrics.tbt_ms ?? 180;
  const si = metrics.si_ms ?? 2200;
  const cls = metrics.cls ?? 0.05;
  const rowScores = entry.scores as {
    performance: number;
    accessibility: number;
    best_practices: number;
    seo: number;
  };
  const scores = {
    performance: rowScores.performance / 100,
    accessibility: rowScores.accessibility / 100,
    best_practices: rowScores.best_practices / 100,
    seo: rowScores.seo / 100,
  };

  // Observed (trace) timings sit under the simulated (lantern) numbers —
  // the waterfall + filmstrip live on the observed timeline.
  const obsFcp = Math.round(fcp * 0.62);
  const obsLcp = Math.round(lcp * 0.6);

  const req = (
    u: string,
    start: number,
    reqTime: number,
    end: number,
    size: number,
    type: string,
    mime: string,
  ) => ({
    url: u,
    rendererStartTime: start,
    networkRequestTime: reqTime,
    networkEndTime: end,
    transferSize: size,
    resourceSize: Math.round(size * 3.1),
    statusCode: 200,
    protocol: 'h2',
    priority: 'High',
    resourceType: type,
    mimeType: mime,
  });

  const networkItems = [
    req(url, 0, 4, ttfb * 0.6 + 110, 14200, 'Document', 'text/html'),
    req(
      `${origin}/assets/app.css`,
      ttfb * 0.6 + 118,
      ttfb * 0.6 + 122,
      obsFcp * 0.72,
      18400,
      'Stylesheet',
      'text/css',
    ),
    req(
      `${origin}/assets/app.js`,
      ttfb * 0.6 + 120,
      ttfb * 0.6 + 126,
      obsFcp * 0.94,
      96400,
      'Script',
      'application/javascript',
    ),
    req(
      `${origin}/fonts/inter-var.woff2`,
      obsFcp * 0.55,
      obsFcp * 0.58,
      obsFcp * 0.9,
      32800,
      'Font',
      'font/woff2',
    ),
    req(
      `${origin}/images/hero-fern.webp`,
      obsFcp * 0.7,
      obsFcp * 0.74,
      obsLcp,
      148000,
      'Image',
      'image/webp',
    ),
    req(
      `${origin}/assets/product-grid.js`,
      obsFcp,
      obsFcp + 8,
      obsLcp * 0.92,
      41200,
      'Script',
      'application/javascript',
    ),
    req(
      'https://www.googletagmanager.com/gtm.js?id=GTM-DEMO',
      obsFcp * 0.8,
      obsFcp * 0.84,
      obsLcp * 1.06,
      42100,
      'Script',
      'application/javascript',
    ),
    req(
      'https://www.google-analytics.com/analytics.js',
      obsLcp * 0.9,
      obsLcp * 0.92,
      obsLcp * 1.12,
      20300,
      'Script',
      'application/javascript',
    ),
    req(
      'https://connect.facebook.net/en_US/fbevents.js',
      obsLcp * 0.95,
      obsLcp * 0.98,
      obsLcp * 1.2,
      24800,
      'Script',
      'application/javascript',
    ),
    req(
      'https://widget.intercom.io/widget/demo',
      obsLcp * 1.02,
      obsLcp * 1.05,
      obsLcp * 1.45,
      61200,
      'Script',
      'application/javascript',
    ),
    req(
      'https://js.stripe.com/v3/',
      obsLcp * 1.1,
      obsLcp * 1.14,
      obsLcp * 1.4,
      31500,
      'Script',
      'application/javascript',
    ),
    req(
      `${origin}/api/collections/featured`,
      obsLcp * 1.05,
      obsLcp * 1.08,
      obsLcp * 1.3,
      6800,
      'XHR',
      'application/json',
    ),
    req(
      `${origin}/images/grid-1.webp`,
      obsLcp * 1.1,
      obsLcp * 1.12,
      obsLcp * 1.5,
      52200,
      'Image',
      'image/webp',
    ),
    req(
      `${origin}/images/grid-2.webp`,
      obsLcp * 1.12,
      obsLcp * 1.15,
      obsLcp * 1.55,
      49800,
      'Image',
      'image/webp',
    ),
  ];

  const lcpNode = {
    selector: 'main > section.hero > img.hero-media',
    nodeLabel: 'Fern & Flow — hero product photograph',
    snippet: '<img class="hero-media" src="/images/hero-fern.webp" alt="Fern & Flow planter">',
  };

  const frames = Array.from({ length: 8 }, (_, i) => ({
    timing: Math.round(((i + 1) / 8) * obsLcp * 1.3),
    timestamp: Math.round(((i + 1) / 8) * obsLcp * 1.3) * 1000,
    data: FRAME_PNG,
  }));

  const perfRefs = [
    { id: 'render-blocking-resources', group: 'load-opportunities' },
    { id: 'unused-javascript', group: 'load-opportunities' },
    { id: 'modern-image-formats', group: 'load-opportunities' },
    { id: 'mainthread-work-breakdown', group: 'diagnostics' },
    { id: 'third-party-summary', group: 'diagnostics' },
    { id: 'server-response-time', group: 'diagnostics' },
  ];

  return {
    lighthouseVersion: '13.0.1',
    requestedUrl: url,
    finalDisplayedUrl: url,
    fetchTime: entry.created_at,
    categories: {
      performance: {
        id: 'performance',
        title: 'Performance',
        score: scores.performance,
        auditRefs: perfRefs,
      },
      accessibility: {
        id: 'accessibility',
        title: 'Accessibility',
        score: scores.accessibility,
        auditRefs: [{ id: 'color-contrast' }, { id: 'image-alt' }],
      },
      'best-practices': {
        id: 'best-practices',
        title: 'Best Practices',
        score: scores.best_practices,
        auditRefs: [{ id: 'errors-in-console' }],
      },
      seo: { id: 'seo', title: 'SEO', score: scores.seo, auditRefs: [] },
    },
    audits: {
      metrics: {
        id: 'metrics',
        title: 'Metrics',
        score: 1,
        details: {
          items: [
            {
              largestContentfulPaint: lcp,
              firstContentfulPaint: fcp,
              cumulativeLayoutShift: cls,
              totalBlockingTime: tbt,
              speedIndex: si,
              interactive: Math.round(lcp * 1.35),
              observedFirstContentfulPaint: obsFcp,
              observedLargestContentfulPaint: obsLcp,
            },
          ],
        },
      },
      'server-response-time': {
        id: 'server-response-time',
        title: 'Initial server response time was short',
        score: ttfb < 600 ? 1 : 0.75,
        displayValue: `Root document took ${Math.round(ttfb * 0.6)} ms`,
        numericValue: Math.round(ttfb * 0.6),
      },
      'network-requests': {
        id: 'network-requests',
        title: 'Network Requests',
        score: null,
        details: { type: 'table', items: networkItems },
      },
      'largest-contentful-paint-element': {
        id: 'largest-contentful-paint-element',
        title: 'Largest Contentful Paint element',
        score: null,
        displayValue: `${(lcp / 1000).toFixed(1)}\u00a0s`,
        details: { type: 'table', items: [{ node: lcpNode }] },
      },
      'lcp-breakdown-insight': {
        id: 'lcp-breakdown-insight',
        title: 'LCP breakdown',
        score: null,
        details: {
          type: 'list',
          items: [
            {
              type: 'table',
              items: [
                {
                  subpart: 'timeToFirstByte',
                  label: 'Time to first byte',
                  duration: Math.round(lcp * 0.24),
                },
                {
                  subpart: 'resourceLoadDelay',
                  label: 'Resource load delay',
                  duration: Math.round(lcp * 0.18),
                },
                {
                  subpart: 'resourceLoadDuration',
                  label: 'Resource load duration',
                  duration: Math.round(lcp * 0.34),
                },
                {
                  subpart: 'elementRenderDelay',
                  label: 'Element render delay',
                  duration: Math.round(lcp * 0.24),
                },
              ],
            },
            { type: 'node', ...lcpNode },
          ],
        },
      },
      'mainthread-work-breakdown': {
        id: 'mainthread-work-breakdown',
        title: 'Minimize main-thread work',
        score: tbt > 200 ? 0.72 : 0.95,
        displayValue: `${((tbt * 6.5) / 1000).toFixed(1)}\u00a0s`,
        details: {
          items: [
            {
              group: 'scriptEvaluation',
              groupLabel: 'Script Evaluation',
              duration: Math.round(tbt * 2.6),
            },
            { group: 'styleLayout', groupLabel: 'Style & Layout', duration: Math.round(tbt * 1.4) },
            { group: 'other', groupLabel: 'Other', duration: Math.round(tbt * 1.2) },
            { group: 'parseHTML', groupLabel: 'Parse HTML & CSS', duration: Math.round(tbt * 0.8) },
            {
              group: 'paintCompositeRender',
              groupLabel: 'Rendering',
              duration: Math.round(tbt * 0.5),
            },
          ],
        },
      },
      'network-dependency-tree-insight': {
        id: 'network-dependency-tree-insight',
        title: 'Network dependency tree',
        score: null,
        details: {
          type: 'list',
          items: [
            {
              type: 'list-section',
              value: {
                type: 'network-tree',
                longestChain: { duration: Math.round(obsLcp * 1.1) },
                chains: {
                  root: {
                    url,
                    navStartToEndTime: Math.round(ttfb * 0.6) + 110,
                    transferSize: 14200,
                    isLongest: true,
                    children: {
                      css: {
                        url: `${origin}/assets/app.css`,
                        navStartToEndTime: Math.round(obsFcp * 0.72),
                        transferSize: 18400,
                        isLongest: false,
                        children: {},
                      },
                      js: {
                        url: `${origin}/assets/app.js`,
                        navStartToEndTime: Math.round(obsFcp * 0.94),
                        transferSize: 96400,
                        isLongest: true,
                        children: {
                          hero: {
                            url: `${origin}/images/hero-fern.webp`,
                            navStartToEndTime: obsLcp,
                            transferSize: 148000,
                            isLongest: true,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          ],
        },
      },
      'render-blocking-insight': {
        id: 'render-blocking-insight',
        title: 'Render blocking requests',
        score: 0.68,
        details: {
          items: [
            { url: `${origin}/assets/app.css` },
            { url: 'https://www.googletagmanager.com/gtm.js?id=GTM-DEMO' },
          ],
          overallSavingsMs: 180,
        },
      },
      'render-blocking-resources': {
        id: 'render-blocking-resources',
        title: 'Eliminate render-blocking resources',
        score: 0.68,
        displayValue: 'Potential savings of 180\u00a0ms',
        description:
          'Resources are blocking the first paint of your page. Consider delivering critical JS/CSS inline and deferring all non-critical JS/styles.',
        details: { overallSavingsMs: 180, items: [{ url: `${origin}/assets/app.css` }] },
      },
      'unused-javascript': {
        id: 'unused-javascript',
        title: 'Reduce unused JavaScript',
        score: 0.55,
        displayValue: 'Potential savings of 58\u00a0KiB',
        description:
          'Reduce unused JavaScript and defer loading scripts until they are required to decrease bytes consumed by network activity.',
        details: {
          overallSavingsBytes: 59400,
          overallSavingsMs: 240,
          items: [{ url: 'https://widget.intercom.io/widget/demo' }],
        },
      },
      'modern-image-formats': {
        id: 'modern-image-formats',
        title: 'Serve images in next-gen formats',
        score: 0.82,
        displayValue: 'Potential savings of 22\u00a0KiB',
        description:
          'Image formats like WebP and AVIF often provide better compression than PNG or JPEG, which means faster downloads and less data consumption.',
        details: {
          overallSavingsBytes: 22600,
          overallSavingsMs: 90,
          items: [{ url: `${origin}/images/grid-2.webp` }],
        },
      },
      'third-party-summary': {
        id: 'third-party-summary',
        title: 'Minimize third-party usage',
        score: null,
        displayValue: 'Third-party code blocked the main thread for 210\u00a0ms',
        details: {
          type: 'table',
          items: [
            { entity: 'Intercom', transferSize: 61200, blockingTime: 120, mainThreadTime: 480 },
            {
              entity: 'Google Tag Manager',
              transferSize: 42100,
              blockingTime: 60,
              mainThreadTime: 320,
            },
            { entity: 'Meta Pixel', transferSize: 24800, blockingTime: 30, mainThreadTime: 210 },
            {
              entity: 'Google Analytics',
              transferSize: 20300,
              blockingTime: 0,
              mainThreadTime: 140,
            },
            { entity: 'Stripe', transferSize: 31500, blockingTime: 0, mainThreadTime: 90 },
          ],
        },
      },
      'screenshot-thumbnails': {
        id: 'screenshot-thumbnails',
        title: 'Screenshot Thumbnails',
        score: null,
        details: { type: 'filmstrip', items: frames },
      },
      'color-contrast': {
        id: 'color-contrast',
        title: 'Background and foreground colors have a sufficient contrast ratio',
        score: 0.85,
        description: 'Low-contrast text is difficult or impossible for many users to read.',
      },
      'image-alt': {
        id: 'image-alt',
        title: 'Image elements have [alt] attributes',
        score: 1,
      },
      'errors-in-console': {
        id: 'errors-in-console',
        title: 'No browser errors logged to the console',
        score: 0.9,
      },
    },
  };
}

function buildPagesWithAudits(): unknown {
  return PAGES.slice(0, 6).map((p, i) => ({
    uuid: `pg-demo${i}`,
    site_uuid: DEMO_SITE_UUID,
    url: `https://lumina.store${p.path}`,
    name: p.path === '/' ? 'Home' : (p.path.split('/').pop() ?? p.path),
    enabled: true,
    created_at: SITE.created_at,
    updated_at: SITE.created_at,
    recent_audits: Array.from({ length: 8 }, (_, k) => ({
      // The newest row carries a resolvable demo audit id (page i's
      // mobile audit) so /seo's worst-pages links land on a real
      // audit detail page in demo mode.
      snapshot_uuid: k === 7 ? `aud_demo${2 * i + 1}` : `snp-${i}-${k}`,
      timestamp: iso((7 - k) * 3),
      metrics: {
        performance: p.lcp < 2500 ? 0.9 : 0.78,
        // 0–100 like the real API (category scores are scaled at the
        // route). Fixed per page so /seo's worst-first ranking is stable.
        seo: [96, 62, 91, 84, 73, 94][i] ?? 90,
        lcp_ms: p.lcp,
        cls: 0.05,
        inp_ms: 160,
        ttfb_ms: 620,
        fcp_ms: 1400,
      },
    })),
  }));
}

function buildThirdParties(): unknown {
  const ents: ReadonlyArray<readonly [string, number, string]> = [
    ['Google Tag Manager', 320, 'needs_work'],
    ['Google Analytics', 140, 'good'],
    ['Meta Pixel', 210, 'needs_work'],
    ['Intercom', 480, 'poor'],
    ['Stripe', 90, 'good'],
    ['Hotjar', 260, 'needs_work'],
    ['YouTube', 540, 'poor'],
  ];
  const entities = ents.map(([entity, ms, bucket]) => ({
    entity,
    avg_main_thread_ms: ms,
    p75_main_thread_ms: Math.round(ms * 1.3),
    total_main_thread_ms: ms * 8,
    avg_ttfb_ms: 120 + Math.round(seeded(entity) * 180),
    transfer_kb: Math.round(40 + seeded(entity) * 260),
    avg30_main_thread_ms: Math.round(ms * 1.05),
    avg30_ttfb_ms: 140,
    avg30_transfer_kb: 120,
    pages_seen: 6,
    request_count: 2 + Math.round(seeded(entity) * 6),
    bucket,
  }));
  return {
    metric: 'js',
    region: null,
    days: 28,
    total_pages: 8,
    thresholds: { good_max: 150, needs_work_max: 350, unit: 'ms' },
    entities,
  };
}

// ─── Traffic ───────────────────────────────────────────────────────────────

function buildKpisTrend(days: number): unknown {
  const rows: Array<Record<string, number | string>> = [];
  for (let d = Math.min(days, 90) - 1; d >= 0; d -= 1) {
    const dayStr = ymd(dayOffset(d));
    const base = 380 + Math.round(seeded(`${dayStr}v`) * 220);
    rows.push({
      day: dayStr,
      visitors: base,
      sessions: Math.round(base * 1.32),
      pageviews: Math.round(base * 3.8),
      pages_per_session: round2(2.6 + seeded(dayStr) * 0.6),
      bounce_rate: round2(0.36 + seeded(`${dayStr}b`) * 0.12),
      avg_session_duration_ms: 120_000 + Math.round(seeded(`${dayStr}d`) * 40_000),
    });
  }
  return rows;
}

function buildBundle(surfaces: string[], days: number): unknown {
  const bundle: Record<string, unknown> = {};
  if (surfaces.length === 0 || surfaces.includes('kpis')) {
    bundle.kpis = {
      visitors: 12_840,
      sessions: 16_920,
      pageviews: 48_610,
      pages_per_session: 2.87,
      bounce_rate: 0.41,
      avg_session_duration_ms: 138_000,
    };
  }
  if (surfaces.length === 0 || surfaces.includes('kpis_trend'))
    bundle.kpis_trend = buildKpisTrend(days);
  if (surfaces.length === 0 || surfaces.includes('health')) bundle.health = health();
  return bundle;
}

function buildPixelTrend(days: number): unknown {
  const rows: Array<Record<string, number | string>> = [];
  for (let d = Math.min(days, 90) - 1; d >= 0; d -= 1) {
    const dayStr = ymd(dayOffset(d));
    for (const ff of FORM_FACTORS) {
      const base = ff === 'phone' ? 240 : 150;
      const v = base + Math.round(seeded(`${dayStr}${ff}`) * 120);
      rows.push({
        day: dayStr,
        form_factor: ff,
        visitors: v,
        sessions: Math.round(v * 1.3),
        pageviews: Math.round(v * 3.6),
      });
    }
  }
  return rows;
}

function buildTopPages(): unknown {
  const row = (path: string, pv: number) => ({
    url: `https://lumina.store${path}`,
    pageviews: pv,
    entries: Math.round(pv * 0.42),
    exits: Math.round(pv * 0.36),
    visitors: Math.round(pv * 0.7),
    sessions: Math.round(pv * 0.8),
    avg_engagement_ms: 40_000 + Math.round(seeded(path) * 80_000),
  });
  return [
    ...PAGES.map((p) => row(p.path, Math.round(TOTAL_PV * p.share))),
    // Long-tail paths that also appear as sitemap-health issues, so
    // /seo/sitemap can demo traffic-weighted "fix this first" ranking.
    row('/collections/aw24', 1240),
    row('/products/lumen-classic', 610),
    row('/collections/lamps', 460),
    row('/about-us', 210),
    row('/checkout/thank-you', 95),
  ];
}

const REFERRERS: ReadonlyArray<readonly [string, number]> = [
  ['google.com', 0.34],
  ['(direct)', 0.28],
  ['instagram.com', 0.12],
  ['facebook.com', 0.09],
  ['bing.com', 0.05],
  ['pinterest.com', 0.05],
  ['t.co', 0.04],
  ['reddit.com', 0.03],
];
const CHANNELS: ReadonlyArray<readonly [string, number]> = [
  ['Organic Search', 0.36],
  ['Direct', 0.28],
  ['Social', 0.2],
  ['Referral', 0.09],
  ['Paid Search', 0.05],
  ['Email', 0.02],
];
const BROWSERS: ReadonlyArray<readonly [string, number]> = [
  ['Chrome', 0.58],
  ['Safari', 0.26],
  ['Edge', 0.08],
  ['Firefox', 0.05],
  ['Samsung Internet', 0.03],
];
const OSES: ReadonlyArray<readonly [string, number]> = [
  ['iOS', 0.38],
  ['Android', 0.3],
  ['Windows', 0.2],
  ['macOS', 0.1],
  ['Linux', 0.02],
];
const REGIONS: ReadonlyArray<readonly [string, number]> = [
  ['California', 0.14],
  ['New York', 0.1],
  ['Texas', 0.08],
  ['Florida', 0.07],
  ['England', 0.09],
  ['Bavaria', 0.05],
];

function breakdownRows(items: ReadonlyArray<readonly [string, number]>): PixelBreakdownRow[] {
  return items.map(([bucket, share]) => {
    const pv = Math.round(TOTAL_PV * share);
    return {
      bucket,
      pageviews: pv,
      visitors: Math.round(pv * 0.7),
      sessions: Math.round(pv * 0.8),
      lcp_p75: 2000 + Math.round(seeded(bucket) * 900),
      lcp_good_density: round2(0.6 + seeded(`${bucket}g`) * 0.3),
    };
  });
}

function buildBreakdownDim(dimension: string): PixelBreakdownRow[] {
  switch (dimension) {
    case 'country_code':
      return buildBreakdown('country_code');
    case 'referrer_host':
      return breakdownRows(REFERRERS);
    case 'channel':
      return breakdownRows(CHANNELS);
    case 'form_factor':
      return breakdownRows([
        ['phone', 0.62],
        ['desktop', 0.33],
        ['tablet', 0.05],
      ]);
    case 'browser_family':
      return breakdownRows(BROWSERS);
    case 'os_family':
      return breakdownRows(OSES);
    case 'region_code':
      return breakdownRows(REGIONS);
    default:
      return breakdownRows([['(not set)', 1]]);
  }
}

function buildDeployConfig(): unknown {
  return {
    site_uuid: DEMO_SITE_UUID,
    enabled: true,
    webhook_url: `https://api.clickweave.co/v1/sites/${DEMO_SITE_UUID}/deploys/webhook`,
    default_ttl_hours: 24,
    control_window_days: 7,
    setup: {
      curl: `curl -X POST https://api.clickweave.co/v1/sites/${DEMO_SITE_UUID}/deploys/mark -H "Authorization: Bearer cwd_demo_xxx" -d '{"commit_sha":"$GIT_SHA","branch":"main"}'`,
    },
  };
}

function buildGoogleScStatus(): unknown {
  // Not connected → Search Console stays a single-call "connect" surface
  // (no fabricated Google data).
  return { available: true, connected: false, email: null, status: null, properties: [] };
}

// ─── Resolver ─────────────────────────────────────────────────────────────

function listSites(): unknown {
  return { sites: [SITE], limit: 200, offset: 0, has_more: false };
}

/**
 * The four starter profiles a real site is seeded with by the
 * `sites_seed_traffic_profiles` trigger, plus one the "user" added so the
 * demo shows both provenances and a `pending` card — the state a profile
 * lands in when it filters on something reporting can't cover yet.
 *
 * Shapes mirror the API's `TrafficProfile`. The demo is read-only, so the
 * editor's writes are refused by the demo API layer as everywhere else.
 */
function buildTrafficProfiles(): unknown[] {
  const now = new Date().toISOString();
  const base = { site_uuid: DEMO_SITE_UUID, created_at: now, updated_at: now };
  return [
    {
      ...base,
      uuid: 'tpr-demo01',
      name: 'Mobile visitors',
      description: 'Everyone on a phone.',
      filters: { form_factor: ['phone'] },
      is_template: true,
      position: 10,
      resolution: 'ready',
    },
    {
      ...base,
      uuid: 'tpr-demo02',
      name: 'Desktop visitors',
      description: 'Everyone on a desktop or laptop.',
      filters: { form_factor: ['desktop'] },
      is_template: true,
      position: 20,
      resolution: 'ready',
    },
    {
      ...base,
      uuid: 'tpr-demo03',
      name: 'Slow connections',
      description:
        'Visitors the browser rates 3G-class or worse — usually the hardest experience on the site.',
      filters: { network_type: ['3g', '2g', 'slow-2g'] },
      is_template: true,
      position: 30,
      resolution: 'ready',
    },
    {
      ...base,
      uuid: 'tpr-demo04',
      name: 'Mobile on slow connections',
      description:
        'Phones the browser rates 3G-class or worse — usually the worst experience on the site.',
      filters: { form_factor: ['phone'], network_type: ['3g', '2g', 'slow-2g'] },
      is_template: true,
      position: 40,
      resolution: 'ready',
    },
    {
      ...base,
      uuid: 'tpr-demo05',
      name: 'Paid social on Safari',
      description: 'Campaign traffic on the browser that reports the least.',
      filters: { channel: ['paid_social'], browser_family: ['Safari'] },
      is_template: false,
      position: 50,
      resolution: 'ready',
    },
  ];
}

function health(): PixelHealthToday {
  return {
    day_utc: ymd(dayOffset(0)),
    sessions_today: 1284,
    visitors_today: 968,
    beacons_today: 15230,
    last_beacon_at: new Date().toISOString(),
  };
}

/**
 * Realtime counts for `/traffic/live`. Unlike every other fixture here
 * this one is deliberately NOT seeded-deterministic: it drifts with the
 * wall clock, so successive polls visibly move. A frozen number under a
 * pulsing "Live" dot reads as a broken page, not a quiet site.
 *
 * Two out-of-phase sine waves rather than a random walk so the demo is
 * reproducible for anyone watching at the same moment, and never jumps
 * by an implausible amount between two 15s polls.
 */
function live(): PixelLive {
  const t = Date.now() / 1000;
  const drift = Math.sin(t / 47) * 0.22 + Math.sin(t / 13) * 0.06;
  const visitors = Math.max(1, Math.round(142 * (1 + drift)));
  // Per-minute pulse: the same wave sampled at each minute boundary, so
  // consecutive polls agree about history and only the newest (partial)
  // minute moves. Newest minute scales by how far through it we are.
  const nowMin = Math.floor(Date.now() / 60_000);
  const minutes = Array.from({ length: 30 }, (_, i) => {
    const minEpoch = (nowMin - 29 + i) * 60_000;
    const mt = minEpoch / 1000;
    const wave = Math.sin(mt / 47) * 0.22 + Math.sin(mt / 13) * 0.06;
    let pv = Math.max(2, Math.round(34 * (1 + wave)));
    let vis = Math.max(1, Math.round(pv * 0.62));
    if (i === 29) {
      const frac = (Date.now() - minEpoch) / 60_000;
      pv = Math.max(1, Math.round(pv * frac));
      vis = Math.max(1, Math.round(vis * frac));
    }
    return {
      minute: `${new Date(minEpoch).toISOString().slice(0, 19)}Z`,
      pageviews: pv,
      visitors: vis,
    };
  });
  const share = (f: number) => Math.max(1, Math.round(visitors * f));
  return {
    active_visitors: visitors,
    // Sessions run slightly ahead of visitors (a few people on two
    // devices); events are the beacons those sessions emit.
    active_sessions: Math.round(visitors * 1.12),
    beacons_5m: Math.round(visitors * 8.4),
    minutes,
    top_pages: [
      { label: '/', visitors: share(0.31) },
      { label: '/pricing', visitors: share(0.18) },
      { label: '/blog/edge-rendering', visitors: share(0.12) },
      { label: '/docs/quickstart', visitors: share(0.08) },
      { label: '/changelog', visitors: share(0.05) },
    ],
    sources: [
      { label: '', visitors: share(0.34) },
      { label: 'news.ycombinator.com', visitors: share(0.22) },
      { label: 'google.com', visitors: share(0.17) },
      { label: 'bsky.app', visitors: share(0.07) },
      { label: 'linkedin.com', visitors: share(0.04) },
    ],
    countries: [
      { label: 'US', visitors: share(0.38) },
      { label: 'GB', visitors: share(0.17) },
      { label: 'DE', visitors: share(0.11) },
      { label: 'AU', visitors: share(0.07) },
      { label: 'NL', visitors: share(0.05) },
    ],
    vitals: {
      lcp: { p75: Math.round(1840 * (1 + drift * 0.3)), samples: Math.round(visitors * 0.8) },
      inp: { p75: Math.round(128 * (1 + drift * 0.5)), samples: Math.round(visitors * 0.6) },
      cls: { p75: 0.04, samples: Math.round(visitors * 0.8) },
    },
  };
}

/**
 * Match an `apiFetch` path (with query string) to a fixture. Unknown
 * reads return `[]` (safe for the array-typed consumers on the demo
 * surfaces) with a dev warning so a missing fixture is obvious. The
 * `/embed` surface only exercises the pixel-perf + deploys + sites
 * endpoints enumerated here.
 */
// ─── SEO — sitemap health (/seo) ──────────────────────────────────────────

function buildSitemapHealth(): unknown {
  const issue = (
    path: string,
    status: number | null,
    hops: number,
    noindex: boolean,
    error: string | null,
    finalPath = path,
    head: Record<string, unknown> = {},
  ) => ({
    url: `https://lumina.store${path}`,
    status,
    final_url: `https://lumina.store${finalPath}`,
    redirect_hops: hops,
    noindex,
    error,
    title: null,
    canonical: null,
    canonical_mismatch: false,
    missing_title: false,
    missing_description: false,
    duplicate_title: false,
    ...head,
  });
  return {
    site_uuid: DEMO_SITE_UUID,
    origin: 'https://lumina.store',
    source_url: 'https://lumina.store/sitemap.xml',
    checked_at: new Date(Date.now() - 42 * 60_000).toISOString(),
    cached: true,
    total_urls: 214,
    checked: 200,
    summary: {
      // ok + redirect + broken must equal `checked` — the three are
      // the exclusive per-URL states and feed the distribution bar.
      ok: 185,
      redirect: 11,
      broken: 4,
      noindexed: 2,
      canonical_mismatch: 2,
      missing_title: 1,
      missing_description: 4,
      duplicate_title: 2,
    },
    monitored_in_sitemap: 6,
    pages_monitored: 8,
    monitored_missing: ['https://lumina.store/checkout', 'https://lumina.store/collections/new'],
    // No lastmod dates — the common generator omission, and it demos
    // the plain-English hygiene note under the tiles.
    lastmod_present: 0,
    lastmod_newest: null,
    issues: [
      issue('/collections/aw24', 404, 0, false, null),
      issue('/products/lumen-mini-teal', 410, 0, false, null),
      issue('/blog/holiday-gift-guide-2024', 500, 0, false, null),
      issue('/press-kit', null, 0, false, 'fetch failed: connection timed out'),
      issue('/checkout/thank-you', 200, 0, true, null, '/checkout/thank-you', {
        missing_description: true,
      }),
      issue('/account/wishlist', 200, 0, true, null),
      issue('/products/lumen-classic-black', 200, 0, false, null, '/products/lumen-classic-black', {
        title: 'Lumen Classic — Lumina',
        canonical: 'https://lumina.store/products/lumen-classic',
        canonical_mismatch: true,
        missing_description: true,
      }),
      issue('/products/lumen-mini-white', 200, 0, false, null, '/products/lumen-mini-white', {
        title: 'Lumen Mini — Lumina',
        canonical: 'https://lumina.store/products/lumen-mini',
        canonical_mismatch: true,
      }),
      issue('/collections/lamps', 200, 0, false, null, '/collections/lamps', {
        title: 'Collections — Lumina',
        duplicate_title: true,
      }),
      issue('/collections/lighting', 200, 0, false, null, '/collections/lighting', {
        title: 'Collections — Lumina',
        duplicate_title: true,
        missing_description: true,
      }),
      issue('/landing/summer-sale', 200, 0, false, null, '/landing/summer-sale', {
        missing_title: true,
        missing_description: true,
      }),
      issue('/products/lumen-classic', 200, 1, false, null, '/products/lumen-classic-v2'),
      issue('/collections/sale', 200, 2, false, null, '/collections/outlet'),
      issue('/about-us', 200, 1, false, null, '/about'),
      issue('/blog/feed', 200, 1, false, null, '/blog/rss.xml'),
    ],
  };
}

export async function resolveDemoFixture(path: string): Promise<unknown> {
  // Small artificial latency so skeleton/loading states flash briefly
  // and the surface feels live rather than instantly-hydrated.
  await new Promise((r) => setTimeout(r, 140));

  const url = new URL(path, 'https://demo.local');
  const p = url.pathname;
  const days = Number(url.searchParams.get('days')) || 180;
  const dimension = url.searchParams.get('dimension') ?? '';
  const pagePath = url.searchParams.get('path') ?? '/';
  const formFactor = url.searchParams.get('form_factor') ?? 'ALL';
  const surfaces = (url.searchParams.get('surfaces') ?? '').split(',').filter(Boolean);
  const include = url.searchParams.get('include') ?? '';

  // Org / account bootstrap
  if (p === '/v1/sites') return listSites();
  if (p === '/v1/workspace') return buildWorkspace();
  if (p === '/v1/usage') return buildUsage();
  if (p === '/v1/integrations/google/status') return buildGoogleScStatus();
  {
    // Audit detail + its Lighthouse report — before the bare list route.
    const report = p.match(/^\/v1\/audits\/aud_demo(\d+)\/report\.json$/);
    if (report) return buildLighthouseReport(Number(report[1]));
    const detail = p.match(/^\/v1\/audits\/aud_demo(\d+)$/);
    if (detail) return buildAuditEntry(Number(detail[1]));
  }
  if (p === '/v1/audits') return buildAudits();
  if (p === '/v1/management/device-profiles') return DEVICE_PROFILES;
  if (p === '/v1/regions') return AUDIT_REGIONS;

  // CrUX (Field data) — /crux/* before the bare /crux
  if (p.endsWith('/crux/pages')) return buildCruxPages();
  if (p.endsWith('/crux/geo')) return buildCruxGeo();
  if (p.endsWith('/crux/history')) return buildCruxHistory();
  if (p.endsWith('/crux')) return buildCrux(formFactor);

  // Pixel (Real users + Traffic)
  if (p.endsWith('/pixel/page-traffic')) return buildPageTraffic(pagePath, days);
  if (p.endsWith('/pixel/page-trend')) return buildPageTrafficTrend(pagePath, days);
  if (p.endsWith('/pixel/page-breakdown')) return buildPageBreakdown(pagePath, dimension, days);
  if (p.endsWith('/pixel/page-sessions'))
    return buildPageSessions(
      url.searchParams.get('path') ?? '/',
      Number(url.searchParams.get('limit')) || 50,
    );
  if (p.endsWith('/pixel/session-events'))
    return buildSessionEvents(
      url.searchParams.get('session') ?? 'ses_demo',
      url.searchParams.get('around') ?? '',
    );
  if (p.endsWith('/pixel/profiles')) return buildProfiles();
  // Must sit after `/pixel/profiles` — both end in `/profiles`, and that
  // one is the more specific path.
  if (p.endsWith('/profiles')) return { profiles: buildTrafficProfiles() };
  if (p.endsWith('/pixel/worst-sessions'))
    return buildWorstSessions(days, url.searchParams.get('metric') ?? 'lcp');
  if (p.endsWith('/pixel/perf-trend')) return buildTrend(days);
  if (p.endsWith('/pixel/perf-by-country')) return buildPerfByCountry();
  if (p.endsWith('/pixel/perf-by-page')) return buildPerfByPage();
  if (p.endsWith('/pixel/perf')) {
    const csv = (k: string) => {
      const v = url.searchParams.get(k);
      return v ? v.split(',') : undefined;
    };
    const sd = url.searchParams.get('save_data');
    return buildPerf(days, {
      formFactors: csv('form_factors'),
      countryCodes: csv('country_codes'),
      networkTypes: csv('network_types'),
      ...(sd !== null ? { saveData: Number(sd) } : {}),
      regionCodes: csv('region_codes'),
      browserFamilies: csv('browser_families'),
      osFamilies: csv('os_families'),
      channels: csv('channels'),
      referrerHosts: csv('referrer_hosts'),
      utmSources: csv('utm_sources'),
      utmMediums: csv('utm_mediums'),
      utmCampaigns: csv('utm_campaigns'),
      pagePaths: csv('page_paths'),
    });
  }
  if (p.endsWith('/pixel/bundle')) return buildBundle(surfaces, days);
  if (p.endsWith('/pixel/top-pages')) return buildTopPages();
  if (p.endsWith('/pixel/breakdown')) return buildBreakdownDim(dimension);
  if (p.endsWith('/pixel/trend')) return buildPixelTrend(days);
  if (p.endsWith('/pixel/health')) return health();
  if (p.endsWith('/pixel/live')) return live();
  if (p.endsWith('/pixel/kpis')) return null;

  // SEO (sitemap health card on /seo)
  if (p.endsWith('/seo/sitemap-health')) return buildSitemapHealth();

  // Synthetic audits (Audits tab)
  if (p.endsWith('/metrics/trend')) return buildMetricsTrend(days);
  if (p.endsWith('/third-parties')) return buildThirdParties();
  if (p.endsWith('/pages')) return include.includes('recent_audits') ? buildPagesWithAudits() : [];
  if (p.endsWith('/deploys/config')) return buildDeployConfig();
  if (p.endsWith('/deploys/audit-log')) return { events: [] };
  if (p.endsWith('/deploys')) return { deploys: buildDeploys() };

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[demo-fixtures] no fixture for ${p} — returning null`);
  }
  // `null`, not `[]`: most components treat a nullish `data` as "no
  // data yet" and short-circuit to an empty state, whereas `[]` looks
  // like a valid (array) payload and crashes object-shaped consumers.
  // Un-fixtured surfaces degrade to empty states, backstopped by the
  // per-surface SectionBoundary.
  return null;
}
